/**
 * AnalyseAnnonceForm — Full 17-field form for boat ad analysis.
 *
 * Reusable in hero, dedicated page, blog CTAs.
 * Uses multipart/form-data (supports up to 5 file uploads).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, CheckCircle2, AlertCircle, Upload, X, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BOAT_TYPES = [
  { v: 'voilier', l: 'Voilier' },
  { v: 'moteur', l: 'Bateau à moteur' },
  { v: 'semi-rigide', l: 'Semi-rigide' },
  { v: 'catamaran', l: 'Catamaran' },
  { v: 'autre', l: 'Autre' },
];
const USAGES = [
  { v: 'cotier', l: 'Sorties côtières' },
  { v: 'croisiere', l: 'Croisière' },
  { v: 'peche', l: 'Pêche' },
  { v: 'habitation', l: 'Habitation' },
  { v: 'voyage', l: 'Voyage / tour du monde' },
  { v: 'autre', l: 'Autre' },
];
const EXPERIENCES = [
  { v: 'premier_achat', l: 'Premier achat' },
  { v: 'debutant', l: 'Débutant' },
  { v: 'experimente', l: 'Expérimenté' },
];
const URGENCIES = [
  { v: 'immediat', l: 'Immédiatement' },
  { v: 'moins_3_mois', l: 'Moins de 3 mois' },
  { v: '3_a_6_mois', l: '3 à 6 mois' },
  { v: 'reflexion', l: 'Simple réflexion' },
];
const CONTACTS = [
  { v: 'telephone', l: 'Téléphone' },
  { v: 'email', l: 'Email' },
  { v: 'whatsapp', l: 'WhatsApp' },
];

const initialForm = (initialUrl = '') => ({
  prenom: '',
  nom: '',
  email: '',
  telephone: '',
  lien_annonce: initialUrl,
  type_bateau: '',
  prix_affiche: '',
  localisation: '',
  budget_max: '',
  usage: '',
  experience: '',
  urgence: '',
  question_principale: '',
  preference_contact: 'telephone',
  consent_rgpd: false,
  website: '', // honeypot
});

export default function AnalyseAnnonceForm({ initialUrl = '', compact = false }) {
  const [form, setForm] = useState(() => initialForm(initialUrl));
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState('idle'); // idle | loading | success | error | duplicate
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/analyse-annonce/config`).then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (initialUrl && !form.lien_annonce) setForm((f) => ({ ...f, lien_annonce: initialUrl }));
  }, [initialUrl, form.lien_annonce]);

  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const onFiles = useCallback((e) => {
    const chosen = Array.from(e.target.files || []).slice(0, 5);
    setFiles(chosen);
  }, []);

  const removeFile = (idx) => setFiles((fs) => fs.filter((_, i) => i !== idx));

  const canSubmit = form.prenom && form.nom && form.email && form.telephone && form.lien_annonce
    && form.type_bateau && form.prix_affiche && form.localisation && form.budget_max
    && form.usage && form.experience && form.urgence && form.question_principale
    && form.preference_contact && form.consent_rgpd;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || status === 'loading') return;
    setStatus('loading');
    setError(null);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v === true ? 'true' : v === false ? 'false' : v));
      fd.append('origin_url', window.location.origin);
      files.forEach((f) => fd.append('files', f));
      const { data } = await axios.post(`${API_URL}/api/analyse-annonce/submit`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data.status === 'payment_required' && data.checkout_url) {
        // Paid mode → redirect to Stripe. No further UI state change needed.
        window.location.href = data.checkout_url;
        return;
      }
      setStatus(data.status === 'duplicate' ? 'duplicate' : 'success');
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Une erreur est survenue. Merci de réessayer.');
      setStatus('error');
    }
  };

  if (status === 'success' || status === 'duplicate') {
    return (
      <div className="bg-[#302f2c] border-2 border-[#d9fb06] rounded-2xl p-8 text-center" data-testid="annonce-success">
        <CheckCircle2 className="mx-auto text-[#d9fb06] mb-4" size={64} />
        <h3 className="text-2xl font-black text-[#d9fb06] uppercase mb-3">
          {status === 'duplicate' ? 'Demande déjà envoyée' : 'Votre annonce est bien transmise'}
        </h3>
        <p className="text-[#dfddd6] mb-3">
          {status === 'duplicate'
            ? 'Nous avons déjà reçu une demande similaire de votre part au cours des dernières 24h. Nous vous recontactons rapidement.'
            : `Je vais étudier votre annonce et vous recontacter sous ${config?.delai_reponse_heures || 48}h maximum selon votre préférence de contact.`}
        </p>
        {config?.calendly_url && (
          <div className="mt-6 pt-6 border-t border-[#3f4816]">
            <p className="text-[#dfddd6] text-sm mb-3">
              Envie d&apos;échanger plus tôt ? Réservez un appel découverte de 20 minutes maintenant.
            </p>
            <a
              href={config.calendly_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="calendly-btn-success"
              className="inline-block bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-6 py-3 text-sm font-bold uppercase transition-colors"
            >
              Réserver un appel de 20 min
            </a>
          </div>
        )}
        <p className="text-[#888680] text-xs mt-4">
          Cette première analyse ne remplace pas une inspection physique complète du bateau.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="analyse-annonce-form">
      {config && (
        <div className={`rounded-lg p-3 flex items-start gap-2 text-sm ${
          config.is_free_available
            ? 'bg-[#d9fb06]/10 border border-[#d9fb06]/30 text-[#d9fb06]'
            : 'bg-[#302f2c] border border-[#3f4816] text-[#dfddd6]'
        }`}>
          <Info size={16} className="flex-shrink-0 mt-0.5" />
          <p>
            {config.is_free_available
              ? `Analyse d'annonce offerte : il reste ${config.free_remaining} gratuité${config.free_remaining > 1 ? 's' : ''} (opération de lancement). Ensuite 49 € pour 3 annonces.`
              : `Tarif de l'analyse : ${new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(config.price_cents / 100)} — montant déduit si vous commandez ensuite un audit ou un accompagnement.`}
          </p>
        </div>
      )}

      {/* Honeypot */}
      <input type="text" name="website" value={form.website} onChange={set('website')}
        className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Prénom *" testId="annonce-prenom"><input required type="text" value={form.prenom} onChange={set('prenom')} className={inputCls} maxLength={100} /></Field>
        <Field label="Nom *" testId="annonce-nom"><input required type="text" value={form.nom} onChange={set('nom')} className={inputCls} maxLength={100} /></Field>
        <Field label="Email *" testId="annonce-email"><input required type="email" value={form.email} onChange={set('email')} className={inputCls} maxLength={200} /></Field>
        <Field label="Téléphone *" testId="annonce-telephone"><input required type="tel" value={form.telephone} onChange={set('telephone')} className={inputCls} maxLength={40} placeholder="+33 6 12 34 56 78" /></Field>
      </div>

      <Field label="Lien de l'annonce du bateau *" testId="annonce-lien">
        <input required type="url" value={form.lien_annonce} onChange={set('lien_annonce')} className={inputCls}
          placeholder="https://www.leboncoin.fr/..." />
      </Field>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Type de bateau *" testId="annonce-type"><Select value={form.type_bateau} onChange={set('type_bateau')} options={BOAT_TYPES} placeholder="— Choisir —" /></Field>
        <Field label="Prix affiché *" testId="annonce-prix"><input required type="text" value={form.prix_affiche} onChange={set('prix_affiche')} className={inputCls} placeholder="Ex : 45 000 €" maxLength={100} /></Field>
        <Field label="Localisation du bateau *" testId="annonce-loc"><input required type="text" value={form.localisation} onChange={set('localisation')} className={inputCls} placeholder="Ville, port ou région" maxLength={200} /></Field>
        <Field label="Budget maximal *" testId="annonce-budget"><input required type="text" value={form.budget_max} onChange={set('budget_max')} className={inputCls} placeholder="Ex : 50 000 €" maxLength={100} /></Field>
        <Field label="Usage prévu *" testId="annonce-usage"><Select value={form.usage} onChange={set('usage')} options={USAGES} placeholder="— Choisir —" /></Field>
        <Field label="Expérience *" testId="annonce-experience"><Select value={form.experience} onChange={set('experience')} options={EXPERIENCES} placeholder="— Choisir —" /></Field>
        <Field label="Délai d'achat *" testId="annonce-urgence"><Select value={form.urgence} onChange={set('urgence')} options={URGENCIES} placeholder="— Choisir —" /></Field>
        <Field label="Préférence de contact *" testId="annonce-pref"><Select value={form.preference_contact} onChange={set('preference_contact')} options={CONTACTS} /></Field>
      </div>

      <Field label="Votre principale question ou inquiétude *" testId="annonce-question">
        <textarea required value={form.question_principale} onChange={set('question_principale')} rows={4} maxLength={2000}
          className={inputCls} placeholder="Ex : Le prix me semble bas, le moteur est ancien, l'annonce manque de détails..." />
      </Field>

      {/* Uploads */}
      <div>
        <label className={labelCls}>Documents ou captures d'écran (facultatif — max 5 fichiers, 5 Mo/fichier)</label>
        <label className="mt-2 flex items-center gap-2 border border-dashed border-[#3f4816] hover:border-[#d9fb06] rounded-lg px-4 py-6 cursor-pointer transition-colors bg-[#1a1c1b]">
          <Upload size={20} className="text-[#d9fb06]" />
          <span className="text-sm text-[#888680]">Cliquez pour ajouter vos fichiers (jpg, png, pdf)</span>
          <input type="file" multiple accept=".jpg,.jpeg,.png,.webp,.pdf" onChange={onFiles} className="hidden"
            data-testid="annonce-files-input" />
        </label>
        {files.length > 0 && (
          <ul className="mt-2 space-y-1">
            {files.map((f, i) => (
              <li key={i} className="flex items-center justify-between bg-[#1a1c1b] border border-[#3f4816] rounded px-3 py-2 text-xs">
                <span className="text-[#dfddd6] truncate">{f.name} <span className="text-[#888680]">({(f.size / 1024).toFixed(0)} Ko)</span></span>
                <button type="button" onClick={() => removeFile(i)} className="text-[#888680] hover:text-red-400"><X size={14} /></button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* RGPD */}
      <label className="flex items-start gap-3 cursor-pointer">
        <input type="checkbox" required checked={form.consent_rgpd} onChange={set('consent_rgpd')}
          data-testid="annonce-consent"
          className="mt-1 flex-shrink-0 accent-[#d9fb06]" />
        <span className="text-xs text-[#888680] leading-relaxed">
          J'accepte que mes données soient utilisées pour traiter ma demande d'analyse.{' '}
          <Link to="/politique-confidentialite" target="_blank" className="text-[#d9fb06] underline">Politique de confidentialité</Link>.
          Aucune donnée n'est transmise à des tiers commerciaux.
        </span>
      </label>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2 text-sm text-red-300" data-testid="annonce-error">
          <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <Button type="submit" disabled={!canSubmit || status === 'loading'}
        data-testid="annonce-submit-btn"
        className="w-full bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full py-3 font-bold uppercase disabled:opacity-50 disabled:cursor-not-allowed">
        {status === 'loading' ? (
          <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {config?.is_free_available ? 'Envoi en cours…' : 'Redirection vers le paiement…'}</>
        ) : config && !config.is_free_available ? (
          `Payer ${new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format((config.price_cents || 4900) / 100)} et envoyer`
        ) : (
          'Envoyer mon annonce'
        )}
      </Button>

      {!compact && (
        <p className="text-xs text-[#888680] text-center">
          Réponse sous {config?.delai_reponse_heures || 48}h · Sans engagement · Cette analyse ne remplace pas une inspection physique.
        </p>
      )}
    </form>
  );
}

const inputCls = 'w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-3 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none transition-colors';
const labelCls = 'block text-xs font-bold text-[#d9fb06] mb-1.5 uppercase tracking-wide';

const Field = ({ label, children, testId }) => (
  <label className="block" data-testid={testId}>
    <span className={labelCls}>{label}</span>
    {children}
  </label>
);

const Select = ({ value, onChange, options, placeholder }) => (
  <select required value={value} onChange={onChange} className={inputCls}>
    {placeholder && <option value="">{placeholder}</option>}
    {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
  </select>
);
